// The chain point of a lane light (R3066, v2-the-deep.md §11.4 "The chain
// point"): the client twin of the walk of `server/internal/sim/lanes.go`.
//
// Two lane lights of a faction chain when they stand `lane_chain_px` apart
// along one shipping lane of the faction. The chain points of a light are
// the points of a lane track at that distance along the track from the
// point of the track nearest the light, forward and back, on each track
// that passes within the reach of the light. An open track has none past
// its ends, and a closed track wraps. A chain point is free when no other
// light stands within half the spacing of it. A LAY of a lane light near a
// free chain point lays the light there: the SERVER decides, and the map
// only draws the guide to the same point.
//
// The walk is a hand mirror, so `server/internal/sim/testdata/lane-chain-
// vectors.json` holds both sides to one answer (`lanechain.test.ts`). Keep
// the order of the server: lights in the order given (the sea object id
// order of the store), tracks in order, forward before back, and the first
// of two equal distances wins.

import type { CoursePlot } from './gen/proto/aquaphobia';
import { loopParts } from './ui/plotline';
import { mpxToPx } from './world';

/** One shipping lane as a line, world px: the points in leg order, and a
 *  closed line walks from the last point back to the first. */
export interface ChainTrack { pts: readonly (readonly [number, number])[]; closed: boolean }

/** A wire leg in world px, with the loop flags of the wire. */
export interface ChainLeg { x: number; y: number; loop: boolean; passes: number }

/** A point on the map, world px. */
export interface ChainPt { x: number; y: number }

/**
 * The tracks of one lane from its legs (`AppendLaneTracks` of the sim): the
 * points of the plot, the leg back of a loop part that ends at the last
 * point (a part that opens at the first point closes the line), and the
 * leg back of a part inside the plot as a line of its own.
 */
export function tracksOfLegs(legs: readonly ChainLeg[]): ChainTrack[] {
  const n = legs.length;
  if (n < 2) return [];
  const main: [number, number][] = legs.map((l) => [l.x, l.y]);
  let closed = false;
  const rest: ChainTrack[] = [];
  for (const part of loopParts(legs)) {
    if (part.open >= part.last) continue;
    if (part.last === n - 1 && !closed && main.length === n) {
      if (part.open === 0) closed = true;
      else main.push([legs[part.open].x, legs[part.open].y]);
      continue;
    }
    rest.push({ pts: [[legs[part.last].x, legs[part.last].y], [legs[part.open].x, legs[part.open].y]], closed: false });
  }
  return [{ pts: main, closed }, ...rest];
}

/** The tracks of a lane plot of the wire. */
export function laneTracks(p: CoursePlot): ChainTrack[] {
  return tracksOfLegs(p.legs.map((w) => ({ x: mpxToPx(w.xMpx), y: mpxToPx(w.yMpx), loop: w.loop, passes: w.passes })));
}

function segs(t: ChainTrack): number { return t.closed ? t.pts.length : t.pts.length - 1; }

/** The ends of leg i and its length. */
function leg(t: ChainTrack, i: number): [number, number, number, number, number] {
  const a = t.pts[i], b = t.pts[(i + 1) % t.pts.length];
  return [a[0], a[1], b[0], b[1], Math.hypot(b[0] - a[0], b[1] - a[1])];
}

/** The place along a->b nearest p (0 to 1) and the square of the distance
 *  to it, as `segParam` of the sim. */
function segParam(px: number, py: number, ax: number, ay: number, bx: number, by: number): [number, number] {
  const dx = bx - ax, dy = by - ay;
  const l2 = dx * dx + dy * dy;
  let t = 0;
  if (l2 > 0) t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / l2));
  const qx = ax + dx * t, qy = ay + dy * t;
  return [t, (px - qx) * (px - qx) + (py - qy) * (py - qy)];
}

/** The point of a track at the distance s along it. */
function pointAt(t: ChainTrack, s: number): ChainPt {
  const n = segs(t);
  for (let i = 0; i < n; i++) {
    const [ax, ay, bx, by, l] = leg(t, i);
    if (l <= 0) continue;
    if (s <= l || i === n - 1) {
      const u = s / l;
      return { x: ax + (bx - ax) * u, y: ay + (by - ay) * u };
    }
    s -= l;
  }
  return { x: t.pts[0][0], y: t.pts[0][1] };
}

/** The chain points of a light at (lx, ly) on one track, forward then
 *  back; none when the track does not pass within `reach` of the light. */
export function chainPoints(t: ChainTrack, lx: number, ly: number, reach: number, chain: number): ChainPt[] {
  let at = 0, d2 = Infinity, run = 0, total = 0;
  const n = segs(t);
  for (let i = 0; i < n; i++) {
    const [ax, ay, bx, by, l] = leg(t, i);
    const [u, d] = segParam(lx, ly, ax, ay, bx, by);
    if (d < d2) { at = run + u * l; d2 = d; }
    run += l;
  }
  total = run;
  if (d2 > reach * reach || !(total > 0)) return [];
  const out: ChainPt[] = [];
  for (let s of [at + chain, at - chain]) {
    if (t.closed) {
      s %= total;
      if (s < 0) s += total;
    } else if (s < 0 || s > total) continue;
    out.push(pointAt(t, s));
  }
  return out;
}

/** The square of the distance of two points, as `world.Dist2`. */
function dist2(a: ChainPt, b: ChainPt): number {
  const dx = a.x - b.x, dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/** The free chain point nearest a point, with the index of its light. */
export interface ChainHit extends ChainPt { light: number }

/**
 * The free chain point nearest (x, y) of the lights on the tracks, or
 * null: the walk of `Sim.LaneChainPoint`. A chain point is free when no
 * other light stands within half `chain` of it.
 */
export function chainNearest(tracks: readonly ChainTrack[], lights: readonly ChainPt[], x: number, y: number,
  reach: number, chain: number): ChainHit | null {
  const half = chain / 2, taken = half * half;
  let best = Infinity, hit: ChainHit | null = null;
  lights.forEach((o, li) => {
    for (const t of tracks) {
      for (const c of chainPoints(t, o.x, o.y, reach, chain)) {
        if (lights.some((q, qi) => qi !== li && dist2(q, c) < taken)) continue;
        const d = dist2(c, { x, y });
        if (d < best) { best = d; hit = { x: c.x, y: c.y, light: li }; }
      }
    }
  });
  return hit;
}

/** The free chain points of one light: the ghosts of the guide. */
export function freeChainPoints(tracks: readonly ChainTrack[], lights: readonly ChainPt[], li: number,
  reach: number, chain: number): ChainPt[] {
  const half = chain / 2, taken = half * half, o = lights[li];
  const out: ChainPt[] = [];
  for (const t of tracks) {
    for (const c of chainPoints(t, o.x, o.y, reach, chain)) {
      if (!lights.some((q, qi) => qi !== li && dist2(q, c) < taken)) out.push(c);
    }
  }
  return out;
}
